import React, { useEffect, useState } from "react";

import { Button, LabeledField, Modal, TextInput } from "shared/ui";
import { checkAliasAvailability, createMenuAlias } from "services/aliases/rest";
import { MenuAliasDto } from "services/aliases/restModels";

import { useMenuSettingsContext } from "features/MenuSettings/context";
import { publicMenuHost } from "features/MenuSettings/lib/publicMenuHost";

import { Hint, Preview } from "../styles";

interface CreateAliasDialogProps {
    open: boolean;
    menuId: number;
    locale: string;
    onClose: () => void;
    onCreated: (alias: MenuAliasDto) => void;
}

export const CreateAliasDialog = ({
    open,
    menuId,
    locale,
    onClose,
    onCreated,
}: CreateAliasDialogProps) => {
    const { notify } = useMenuSettingsContext();


    const [alias, setAlias] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [checking, setChecking] = useState<boolean>(false);
    const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(null);
    const [isAvailable, setIsAvailable] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        if (!open) return;

        setAlias("");
        setTitle("");
        setDescription("");
        setAvailabilityMessage(null);
        setIsAvailable(false);
    }, [open]);

    /*
    * Занятость и формат проверяет бэк — он же владеет чёрным списком и лимитом.
    * Дебаунс, чтобы не дёргать ручку на каждый символ.
    * */
    useEffect(() => {
        if (!alias) {
            setAvailabilityMessage(null);
            setIsAvailable(false);
            return undefined;
        }

        let ignore = false;
        setChecking(true);

        const timer = setTimeout(() => {
            checkAliasAvailability(alias)
                .then((result) => {
                    if (ignore) return;
                    setIsAvailable(!!result && result.isAvailable);
                    setAvailabilityMessage(result ? result.message : null);
                })
                .catch(() => {
                    if (ignore) return;
                    setIsAvailable(false);
                    setAvailabilityMessage("Не удалось проверить ссылку");
                })
                .finally(() => {
                    if (ignore) return;
                    setChecking(false);
                });
        }, 400);

        return () => {
            ignore = true;
            clearTimeout(timer);
        };
    }, [alias]);

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const created = await createMenuAlias(menuId, {
                alias,
                title: { [locale]: title },
                description: { [locale]: description },
            });

            if (created) onCreated(created);
            onClose();
        } catch (e) {
            notify.showError("Не удалось создать ссылку", e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Новая ссылка на меню"
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isAvailable || checking || saving}
                        onClick={handleSubmit}
                    >
                        Создать
                    </Button>
                </>
            }
        >
            <LabeledField
                label="Адрес ссылки"
                hint={checking ? "Проверяем…" : availabilityMessage}
            >
                <TextInput
                    value={alias}
                    onChange={setAlias}
                    placeholder="my-cafe"
                />
            </LabeledField>

            <Preview>
                {publicMenuHost()}/{alias || "…"}
            </Preview>

            <LabeledField label="Название">
                <TextInput
                    value={title}
                    onChange={setTitle}
                    placeholder="Как называется заведение"
                />
            </LabeledField>

            <LabeledField label="Описание">
                <TextInput
                    value={description}
                    onChange={setDescription}
                    placeholder="Короткое описание для превью ссылки"
                />
            </LabeledField>

            <Hint>Ссылку увидят гости — её нельзя будет изменить, только удалить.</Hint>
        </Modal>
    );
};
